import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { homeApi } from '../api/api';

const EMPTY_UPI = {
  payeeName: '',
  upiNumber: '',
  upiId: '',
  qrImage: '',
};

const EMPTY_BANK = {
  accountName: '',
  accountNumber: '',
  ifsc: '',
  bankName: '',
  branch: '',
};

const BANK_FIELD_KEYS = ['accountName', 'accountNumber', 'ifsc', 'bankName', 'branch'];

const EMPTY_SEVA_CONTACT = {
  phone: '',
  email: '',
};

function buildUpiPayUrl(upiId, payeeName, note) {
  return `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(payeeName)}&cu=INR&tn=${encodeURIComponent(note)}`;
}

function buildUpiQrImage(payUrl) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=10&data=${encodeURIComponent(payUrl)}`;
}

function toTelHref(phone) {
  const cleaned = String(phone || '').replace(/[^\d+]/g, '');
  return cleaned ? `tel:${cleaned}` : '#';
}

function mergeObj(raw, fallback) {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const next = { ...fallback };
  Object.keys(fallback).forEach((k) => {
    const v = raw[k] != null ? String(raw[k]).trim() : '';
    if (v) next[k] = v;
  });
  return next;
}

/** Works on HTTP / mobile where navigator.clipboard is unavailable */
const copyText = async (text) => {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function' && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (e) {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
};

/** Load QR as File for Web Share Level 2 (native share with image) */
const imageUrlToFile = async (url, filename = 'seva-upi-qr.png') => {
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'force-cache' });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/png';
    return new File([blob], filename, { type });
  } catch (e) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || 400;
          canvas.height = img.naturalHeight || 400;
          canvas.getContext('2d').drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('toBlob failed'));
              return;
            }
            resolve(new File([blob], filename, { type: 'image/png' }));
          }, 'image/png');
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('image load failed'));
      img.src = url;
    });
  }
};

/**
 * Opens Android/iOS native system share UI.
 * Tries: files+text → files only → text only (Android Chrome quirks).
 */
const openNativeShare = async (text, title, file) => {
  if (typeof navigator.share !== 'function') {
    return 'unsupported';
  }

  const tryShare = async (payload) => {
    if (navigator.canShare && !navigator.canShare(payload)) {
      return false;
    }
    await navigator.share(payload);
    return true;
  };

  if (file) {
    try {
      const withFileAndText = { title, text, files: [file] };
      if (await tryShare(withFileAndText)) return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
    }

    try {
      const filesOnly = { files: [file], title };
      if (await tryShare(filesOnly)) return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
    }
  }

  try {
    await navigator.share({ title, text });
    return 'shared';
  } catch (err) {
    if (err?.name === 'AbortError') return 'cancelled';
    return 'failed';
  }
};

const SHOW_TAX_BENEFITS = false;

const Donate = () => {
  const { t } = useTranslation();
  const [upi, setUpi] = useState(EMPTY_UPI);
  const [bank, setBank] = useState(EMPTY_BANK);
  const [sevaContact, setSevaContact] = useState(EMPTY_SEVA_CONTACT);
  const [copiedUpi, setCopiedUpi] = useState(false);
  const [copiedUpiNumber, setCopiedUpiNumber] = useState(false);
  const [copiedBankField, setCopiedBankField] = useState(null);
  const [copiedBankAll, setCopiedBankAll] = useState(false);
  const [shareBankStatus, setShareBankStatus] = useState(null);
  const [shareUpiStatus, setShareUpiStatus] = useState(null);
  const [qrFullscreen, setQrFullscreen] = useState(false);
  const qrFileRef = useRef(null);

  const upiPayNote = t('donatePage.upi.pay_note');
  const upiShareTitle = t('donatePage.upi.share_title');
  const bankShareTitle = t('donatePage.bank.share_title');

  const upiPayUrl = useMemo(() => {
    if (!(upi.upiId || '').trim()) return '';
    return buildUpiPayUrl(upi.upiId, upi.payeeName || 'Seva', upiPayNote);
  }, [upi.upiId, upi.payeeName, upiPayNote]);
  const upiQrImage = useMemo(() => {
    const custom = (upi.qrImage || '').trim();
    if (custom) return custom;
    if (!upiPayUrl) return '';
    return buildUpiQrImage(upiPayUrl);
  }, [upi.qrImage, upiPayUrl]);

  const bankFields = useMemo(
    () => BANK_FIELD_KEYS.map((key) => ({
      key,
      label: t(`donatePage.bank.fields.${key}`),
      value: bank[key] || '',
    })),
    [bank, t]
  );

  const upiShareText = useMemo(
    () => t('donatePage.upi.share_body', {
      number: upi.upiNumber,
      id: upi.upiId,
      name: upi.payeeName,
    }),
    [upi, t]
  );

  const bankShareText = useMemo(
    () => t('donatePage.bank.share_body', {
      details: bankFields.map((f) => `${f.label}: ${f.value}`).join('\n\n'),
    }),
    [bankFields, t]
  );

  const shareUpiLabel = shareUpiStatus === 'shared'
    ? t('donatePage.upi.shared')
    : shareUpiStatus === 'copied'
      ? t('donatePage.upi.copied')
      : t('donatePage.upi.share');

  const shareBankLabel = shareBankStatus === 'shared'
    ? t('donatePage.bank.shared')
    : shareBankStatus === 'copied'
      ? t('donatePage.bank.copied')
      : t('donatePage.bank.share');

  useEffect(() => {
    homeApi
      .getAll()
      .then((res) => {
        if (!res.success || !res.data) return;
        const d = res.data;
        setUpi(mergeObj(d.seva_upi, EMPTY_UPI));
        setBank(mergeObj(d.seva_bank, EMPTY_BANK));
        const phones = d.ashram_phones && typeof d.ashram_phones === 'object' ? d.ashram_phones : {};
        const emails = d.ashram_emails && typeof d.ashram_emails === 'object' ? d.ashram_emails : {};
        setSevaContact({
          phone: (phones.sevaCoordination || '').trim(),
          email: (emails.seva || '').trim(),
        });
      })
      .catch(() => {
        setUpi(EMPTY_UPI);
        setBank(EMPTY_BANK);
        setSevaContact(EMPTY_SEVA_CONTACT);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    qrFileRef.current = null;
    if (!upiQrImage) return undefined;
    imageUrlToFile(upiQrImage)
      .then((file) => {
        if (!cancelled) qrFileRef.current = file;
      })
      .catch(() => {
        // Share will fall back to text-only
      });
    return () => {
      cancelled = true;
    };
  }, [upiQrImage]);

  const getBankDetailsText = () =>
    bankFields.map((f) => `${f.label}: ${f.value}`).join('\n\n');

  const upiShareFullText = `${upiShareTitle}\n\n${upiShareText}`;
  const bankShareFullText = `${bankShareTitle}\n\n${bankShareText}`;

  const handleShareUpi = async () => {
    let file = qrFileRef.current;
    if (!file) {
      try {
        file = await imageUrlToFile(upiQrImage);
        qrFileRef.current = file;
      } catch (e) {
        file = null;
      }
    }

    const result = await openNativeShare(upiShareFullText, upiShareTitle, file);
    if (result === 'shared') {
      setShareUpiStatus('shared');
      setTimeout(() => setShareUpiStatus(null), 2500);
      return;
    }
    if (result === 'cancelled') return;
    const ok = await copyText(upiShareFullText);
    if (ok) {
      setShareUpiStatus('copied');
      setTimeout(() => setShareUpiStatus(null), 2500);
    }
  };

  const handleShareBank = async () => {
    const result = await openNativeShare(bankShareFullText, bankShareTitle);
    if (result === 'shared') {
      setShareBankStatus('shared');
      setTimeout(() => setShareBankStatus(null), 2500);
      return;
    }
    if (result === 'cancelled') return;
    const ok = await copyText(bankShareFullText);
    if (ok) {
      setShareBankStatus('copied');
      setTimeout(() => setShareBankStatus(null), 2500);
    }
  };

  const handleCopyUpi = async () => {
    if (!(upi.upiId || '').trim()) return;
    const ok = await copyText(upi.upiId);
    if (ok) {
      setCopiedUpi(true);
      setTimeout(() => setCopiedUpi(false), 2000);
    }
  };

  const handleCopyUpiNumber = async () => {
    if (!(upi.upiNumber || '').trim()) return;
    const ok = await copyText(upi.upiNumber);
    if (ok) {
      setCopiedUpiNumber(true);
      setTimeout(() => setCopiedUpiNumber(false), 2000);
    }
  };

  const handleCopyBankField = async (key, value) => {
    if (!(value || '').trim()) return;
    const ok = await copyText(value);
    if (ok) {
      setCopiedBankField(key);
      setTimeout(() => setCopiedBankField(null), 2000);
    }
  };

  const handleCopyBankAll = async () => {
    const text = getBankDetailsText();
    if (!text.trim()) return;
    const ok = await copyText(text);
    if (ok) {
      setCopiedBankAll(true);
      setTimeout(() => setCopiedBankAll(false), 2000);
    }
  };

  const handlePayWithUpi = () => {
    if (!upiPayUrl) return;
    window.location.href = upiPayUrl;
  };

  return (
    <>
      <Helmet>
        <title>{t('donatePage.meta.title')}</title>
        <meta name="description" content={t('donatePage.meta.description')} />
        <link rel="canonical" href="https://www.shreeramranujadham.org/donate" />
        <meta property="og:title" content={t('donatePage.meta.title')} />
        <meta property="og:description" content={t('donatePage.meta.description')} />
        <meta property="og:url" content="https://www.shreeramranujadham.org/donate" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
      </Helmet>

      {/* ── Enhanced Page Header ──────────────────────── */}
      <section
        className="pt-24 pb-6 md:pt-28 md:pb-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 50%, #7c3aed 100%)' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-20 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-20 rounded-full translate-y-40 -translate-x-40"></div>
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-400 bg-opacity-10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-yellow-400 bg-opacity-10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-2 md:mb-8">
            <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
              <i className="fas fa-heart text-yellow-300 mr-3 text-xl"></i>
              <span className="text-white font-semibold">{t('donatePage.hero.badge')}</span>
            </div>
          </div>
          <h1 className="text-2xl md:text-7xl lg:text-8xl font-bold text-white mb-3 md:mb-8 leading-tight drop-shadow-lg">
            {t('donatePage.hero.title_1')}<br />
            <span className="text-yellow-300">{t('donatePage.hero.title_2')}</span>
          </h1>
          <p className="text-xs md:text-xl text-white max-w-3xl mx-auto leading-relaxed mb-8 md:mb-12 drop-shadow-md font-medium">
            {t('donatePage.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* ── Sacred Donation Methods ────────────────────── */}
      <section className="py-16 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-5 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-5 rounded-full translate-y-40 -translate-x-40"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
              <i className="fas fa-donate text-orange-600 mr-3 text-lg"></i>
              <span className="text-orange-700 font-semibold">{t('donatePage.methods.badge')}</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4">{t('donatePage.methods.title')}</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('donatePage.methods.subtitle')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
              {/* UPI Payment */}
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 md:p-8 rounded-2xl shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 text-center group">
                <div className="mb-6">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                    style={{ background: 'linear-gradient(135deg, #FFF7ED, #FFFBEB, #FEF2F2)' }}
                  >
                    <i className="fas fa-mobile-alt text-2xl text-orange-600"></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-800">{t('donatePage.upi.title')}</h3>
                  <p className="text-sm text-gray-600 mt-2">{t('donatePage.upi.subtitle')}</p>
                </div>
                <div className="bg-white p-4 rounded-xl mb-6 shadow-inner border border-orange-100">
                  {upiQrImage ? (
                    <button
                      type="button"
                      onClick={() => setQrFullscreen(true)}
                      className="w-32 h-32 md:w-40 md:h-40 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-orange-300 transition-all border-0 p-0 overflow-hidden"
                      title={t('donatePage.upi.qr_title')}
                      aria-label={t('donatePage.upi.qr_aria')}
                    >
                      <img
                        src={upiQrImage}
                        alt={t('donatePage.upi.qr_alt')}
                        className="w-full h-full object-contain rounded"
                      />
                    </button>
                  ) : (
                    <div className="w-32 h-32 md:w-40 md:h-40 bg-gray-100 rounded-lg mx-auto mb-4" aria-hidden="true" />
                  )}
                  <p className="text-sm text-gray-700">
                    <strong>{t('donatePage.upi.name_label')}</strong> {upi.payeeName}
                  </p>
                </div>
                <div className="space-y-3 mb-6 text-sm text-left">
                  <div className="bg-white py-4 px-3 rounded-lg border border-orange-100">
                    <button
                      type="button"
                      onClick={handleCopyUpiNumber}
                      className="w-full flex items-center justify-between gap-2 text-gray-700 bg-transparent border-0 p-0 cursor-pointer text-left hover:text-orange-600 transition-colors"
                      title={t('donatePage.upi.copy_number')}
                      aria-label={t('donatePage.upi.copy_number')}
                    >
                      <span>
                        <strong>{t('donatePage.upi.number_label')}</strong> {upi.upiNumber}
                        {copiedUpiNumber && (
                          <span className="ml-2 text-green-600 font-semibold">{t('donatePage.upi.copied')}</span>
                        )}
                      </span>
                      <i className={`fas ${copiedUpiNumber ? 'fa-check text-green-600' : 'fa-copy text-orange-500'} flex-shrink-0`}></i>
                    </button>
                  </div>
                  <div className="bg-white py-4 px-3 rounded-lg border border-orange-100">
                    <button
                      type="button"
                      onClick={handleCopyUpi}
                      className="w-full flex items-center justify-between gap-2 text-gray-700 bg-transparent border-0 p-0 cursor-pointer text-left hover:text-orange-600 transition-colors"
                      title={t('donatePage.upi.copy_id')}
                      aria-label={t('donatePage.upi.copy_id')}
                    >
                      <span>
                        <strong>{t('donatePage.upi.id_label')}</strong> {upi.upiId}
                        {copiedUpi && (
                          <span className="ml-2 text-green-600 font-semibold">{t('donatePage.upi.copied')}</span>
                        )}
                      </span>
                      <i className={`fas ${copiedUpi ? 'fa-check text-green-600' : 'fa-copy text-orange-500'} flex-shrink-0`}></i>
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <a
                    href={upiPayUrl || undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePayWithUpi();
                    }}
                    aria-disabled={!upiPayUrl}
                    className={`w-full px-4 py-3 text-white rounded-full font-bold shadow flex items-center justify-center transition-all duration-200 ${upiPayUrl ? 'hover:opacity-90' : 'opacity-50 pointer-events-none'}`}
                    style={{ background: 'linear-gradient(to right, #f97316, #dc2626)' }}
                  >
                    <i className="fas fa-mobile-alt mr-2"></i>
                    {t('donatePage.upi.pay')}
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyUpi}
                    disabled={!(upi.upiId || '').trim()}
                    className="w-full px-4 py-3 bg-white text-orange-600 border-2 border-orange-200 rounded-full hover:bg-orange-50 transition-all duration-200 font-bold shadow disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <i className={`fas ${copiedUpi ? 'fa-check' : 'fa-copy'} mr-2`}></i>
                    {copiedUpi ? t('donatePage.upi.copied_id') : t('donatePage.upi.copy_id')}
                  </button>
                  <button
                    type="button"
                    onClick={handleShareUpi}
                    disabled={!(upi.upiId || '').trim() && !(upi.upiNumber || '').trim()}
                    className="w-full px-4 py-3 bg-white text-orange-600 border-2 border-orange-200 rounded-full hover:bg-orange-50 transition-all duration-200 font-bold shadow disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <i className={`fas ${shareUpiStatus ? 'fa-check' : 'fa-share-alt'} mr-2`}></i>
                    {shareUpiLabel}
                  </button>
                </div>
              </div>

              {/* Bank Transfer */}
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 md:p-8 rounded-2xl shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 group flex flex-col justify-between">
                <div>
                  <div className="text-center mb-6">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                      style={{ background: 'linear-gradient(135deg, #FFF7ED, #FFFBEB, #FEF2F2)' }}
                    >
                      <i className="fas fa-university text-2xl text-orange-600"></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800">{t('donatePage.bank.title')}</h3>
                    <p className="text-sm text-gray-600 mt-2">{t('donatePage.bank.subtitle')}</p>
                  </div>
                  <div className="space-y-3 text-sm">
                    {bankFields.map((field) => {
                      const isCopied = copiedBankField === field.key;
                      const hasValue = Boolean((field.value || '').trim());
                      return (
                        <div key={field.key} className="bg-white py-4 px-3 rounded-lg border border-orange-100">
                          <strong className="text-gray-800 block mb-1">{field.label}:</strong>
                          <button
                            type="button"
                            onClick={() => handleCopyBankField(field.key, field.value)}
                            disabled={!hasValue}
                            className="w-full flex items-center justify-between gap-2 text-gray-700 bg-transparent border-0 p-0 cursor-pointer text-left hover:text-orange-600 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                            title={t('donatePage.bank.copy_field', { label: field.label })}
                            aria-label={t('donatePage.bank.copy_field', { label: field.label })}
                          >
                            <span>
                              {field.value}
                              {isCopied && (
                                <span className="ml-2 text-green-600 font-semibold">{t('donatePage.bank.copied')}</span>
                              )}
                            </span>
                            <i className={`fas ${isCopied ? 'fa-check text-green-600' : 'fa-copy text-orange-500'} flex-shrink-0`}></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCopyBankAll}
                    disabled={!bankFields.some((f) => (f.value || '').trim())}
                    className="w-full px-4 py-3 bg-white text-orange-600 border-2 border-orange-200 rounded-full hover:bg-orange-50 transition-all duration-200 font-bold shadow disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <i className={`fas ${copiedBankAll ? 'fa-check' : 'fa-copy'} mr-2`}></i>
                    {copiedBankAll ? t('donatePage.bank.copied_details') : t('donatePage.bank.copy_details')}
                  </button>
                  <button
                    type="button"
                    onClick={handleShareBank}
                    disabled={!bankFields.some((f) => (f.value || '').trim())}
                    className="w-full px-4 py-3 bg-white text-orange-600 border-2 border-orange-200 rounded-full hover:bg-orange-50 transition-all duration-200 font-bold shadow disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <i className={`fas ${shareBankStatus ? 'fa-check' : 'fa-share-alt'} mr-2`}></i>
                    {shareBankLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sacred Seva Purpose Section ───────────────── */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-10 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-10 rounded-full translate-y-40 -translate-x-40"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16">
            <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
              <i className="fas fa-hands-praying text-orange-600 mr-3 text-lg"></i>
              <span className="text-orange-700 font-semibold">{t('donatePage.purpose.badge')}</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-6 md:mb-8">{t('donatePage.purpose.title')}</h2>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              {t('donatePage.purpose.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 text-center group">
              <div className="bg-gradient-to-br from-orange-100 to-yellow-100 w-16 md:w-20 h-16 md:h-20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fas fa-om text-2xl md:text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 md:mb-4">{t('donatePage.purpose.dham_title')}</h3>
              <p className="text-sm md:text-base text-gray-750 leading-relaxed">
                {t('donatePage.purpose.dham_desc')}
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 text-center group">
              <div className="bg-gradient-to-br from-orange-100 to-red-100 w-16 md:w-20 h-16 md:h-20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fas fa-book-open text-2xl md:text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 md:mb-4">{t('donatePage.purpose.katha_title')}</h3>
              <p className="text-sm md:text-base text-gray-750 leading-relaxed">
                {t('donatePage.purpose.katha_desc')}
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 text-center group">
              <div className="bg-gradient-to-br from-orange-100 to-red-100 w-16 md:w-20 h-16 md:h-20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fas fa-hands-helping text-2xl md:text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 md:mb-4">{t('donatePage.purpose.human_title')}</h3>
              <p className="text-sm md:text-base text-gray-750 leading-relaxed">
                {t('donatePage.purpose.human_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tax Benefits (hidden — set SHOW_TAX_BENEFITS true to show) ── */}
      {SHOW_TAX_BENEFITS && (
      <section className="py-16 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-5 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-5 rounded-full translate-y-40 -translate-x-40"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center font-poppins">
            <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
              <i className="fas fa-certificate text-orange-600 mr-3 text-lg"></i>
              <span className="text-orange-700 font-semibold">{t('donatePage.tax.badge')}</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-8">{t('donatePage.tax.title')}</h2>

            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 md:p-8 rounded-2xl shadow-lg border border-orange-100">
              <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
                <div className="text-center md:text-left">
                  <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto md:mx-0 mb-4 shadow">
                    <i className="fas fa-receipt text-2xl md:text-3xl text-orange-600"></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">{t('donatePage.tax.exemption_title')}</h3>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    {t('donatePage.tax.exemption_desc')}
                  </p>
                </div>
                <div>
                  <div className="bg-white p-4 md:p-6 rounded-xl shadow-md border border-orange-100">
                    <h4 className="font-semibold text-gray-800 mb-4 text-center">{t('donatePage.tax.registration_title')}</h4>
                    <div className="text-xs md:text-sm text-left space-y-2">
                      <div className="flex justify-between border-b pb-1">
                        <strong className="text-gray-600">{t('donatePage.tax.reg_no')}</strong>
                        <span className="text-gray-850 font-semibold">AABTS1234F20221</span>
                      </div>
                      <div className="flex justify-between border-b pb-1">
                        <strong className="text-gray-600">{t('donatePage.tax.pan')}</strong>
                        <span className="text-gray-850 font-semibold">AABTS1234F</span>
                      </div>
                      <div className="flex justify-between pb-1">
                        <strong className="text-gray-600">{t('donatePage.tax.valid_until')}</strong>
                        <span className="text-gray-850 font-semibold">March 2027</span>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                      <p className="text-xs text-gray-600 text-center font-medium">
                        {t('donatePage.tax.receipt_note')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── Contact for Seva Support ─────────────────── */}
      <section className="pt-8 pb-16 md:py-20 bg-gradient-to-br from-orange-50 to-yellow-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-10 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-10 rounded-full translate-y-40 -translate-x-40"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
              <i className="fas fa-headset text-orange-600 mr-3 text-lg"></i>
              <span className="text-orange-700 font-semibold">{t('donatePage.support.badge')}</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-6">{t('donatePage.support.title')}</h2>
            <p className="text-base md:text-lg text-gray-700 mb-8 md:mb-12">
              {t('donatePage.support.subtitle')}
            </p>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 group">
                <div className="bg-gradient-to-br from-orange-100 to-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="fas fa-phone text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('donatePage.support.call_title')}</h3>
                {sevaContact.phone ? (
                  <a
                    href={toTelHref(sevaContact.phone)}
                    className="block text-gray-700 font-medium mb-2 hover:text-orange-600 transition-colors"
                  >
                    {sevaContact.phone}
                  </a>
                ) : (
                  <p className="block text-gray-700 font-medium mb-2">&nbsp;</p>
                )}
                <p className="text-sm text-gray-600 font-medium">{t('donatePage.support.call_hours')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('donatePage.support.call_note')}</p>
              </div>

              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300 group">
                <div className="bg-gradient-to-br from-orange-100 to-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="fas fa-envelope text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('donatePage.support.email_title')}</h3>
                {sevaContact.email ? (
                  <a
                    href={`mailto:${sevaContact.email}`}
                    className="block text-gray-700 font-medium mb-2 hover:text-orange-600 transition-colors"
                  >
                    {sevaContact.email}
                  </a>
                ) : (
                  <p className="block text-gray-700 font-medium mb-2">&nbsp;</p>
                )}
                <p className="text-sm text-gray-600 font-medium">{t('donatePage.support.email_hours')}</p>
                <p className="text-xs text-gray-500 mt-2">{t('donatePage.support.email_note')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── QR Fullscreen Viewer ───────────────────────── */}
      {qrFullscreen && upiQrImage && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-90 p-6"
          onClick={() => setQrFullscreen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t('donatePage.upi.fullscreen_aria')}
        >
          <button
            type="button"
            onClick={() => setQrFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white bg-opacity-20 text-white flex items-center justify-center hover:bg-opacity-30 border-0 cursor-pointer"
            aria-label={t('donatePage.upi.close')}
          >
            <i className="fas fa-times text-xl"></i>
          </button>
          <img
            src={upiQrImage}
            alt={t('donatePage.upi.qr_alt')}
            className="w-64 h-64 md:w-80 md:h-80 object-contain bg-white rounded-2xl p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="text-white text-sm md:text-base mt-6 text-center">
            {t('donatePage.upi.fullscreen_hint')}
          </p>
        </div>
      )}
    </>
  );
};

export default Donate;
